if (!customElements.get("sticky-cart")) {
  customElements.define(
    "sticky-cart",
    class StickyCart extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.triggerType = this.dataset.triggerType;
        this.triggerOffset = parseInt(this.dataset.triggerOffset) || 500;
        this.showImage = this.dataset.showImage === "true";
        this.loginForPrice = this.dataset.loginForPrice === "true";

        this.container = this.querySelector(".sticky-cart-container");
        this.imageSlot = this.querySelector("[data-sticky-cart-image]");
        this.titleSlot = this.querySelector("[data-sticky-cart-title]");
        this.priceSlot = this.querySelector("[data-sticky-cart-price]");
        this.quantitySlot = this.querySelector("[data-sticky-cart-quantity]");
        this.buttonSlot = this.querySelector("[data-sticky-cart-button]");
        this.errorSlot = this.querySelector("[data-sticky-cart-error]");

        this.mobileDock = null;
        this.mainProductSection = null;
        this.mainAddToCartButton = null;
        this.mainProductForm = null;
        this.mainQuantityInput = null;
        this.productHasVariants = false;

        this.selectOptionsText = this.dataset.selectOptionsText;
        this.errorFadeTimeout = null;
        this.unsubscribeVariantChange = null;

        // Scroll says show it, no bundle bar is holding the slot: both must agree.
        this.wantsVisible = false;
        this.suppressed = false;

        queueMicrotask(() => { if (this.isConnected) this.init(); });
      }

      init() {
        // A template clone's children and attributes are present by this microtask.
        this.container = this.querySelector('.sticky-cart-container');
        this.imageSlot = this.querySelector('[data-sticky-cart-image]');
        this.titleSlot = this.querySelector('[data-sticky-cart-title]');
        this.priceSlot = this.querySelector('[data-sticky-cart-price]');
        this.quantitySlot = this.querySelector('[data-sticky-cart-quantity]');
        this.buttonSlot = this.querySelector('[data-sticky-cart-button]');
        this.errorSlot = this.querySelector('[data-sticky-cart-error]');
        // Don't show if login is required to view price
        if (this.loginForPrice) {
          this.style.display = "none";
          return;
        }

        // Only initialize on product pages
        if (!this.findProductElements()) {
          this.style.display = "none";
          return;
        }

        const purchase = this.mainProductForm.querySelector('cq-purchase-options') || this.mainProductForm.closest('cq-purchase-options');
        if (purchase) { this.initPurchase(purchase); return; }

        this.detectMobileDock();
        this.cloneProductContent();
        this.setupScrollDetection();
        this.setupEventListeners();
        this.setupErrorMessageSync();
        this.setupResizeListener();
        this.watchBundleBar();

        // Subscribe to variant changes
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.productVariantChange, (data) => {
          this.reinitializeAfterVariantChange();
        });
      }

      initPurchase(purchase) {
        this.purchase = purchase;
        // One query for the bar's life, not one per scroll frame (A7 4a).
        this.phoneQuery = window.matchMedia('(max-width: 749px)');
        this.classList.add('cq-sticky-purchase');
        // The bar's scroll-padding-bottom (section-sticky-cart.css) hangs on this class, kept while the bar is hidden
        // too. It replaces a root :has() rule that doubled style recalculation (V4-06 2026-10-14).
        document.documentElement.classList.add('cq-buybar-page');
        this.purchaseEvents = new AbortController();
        const options = { signal: this.purchaseEvents.signal };
        this.inert = true;
        this.setAttribute('aria-hidden', 'true');
        this.priceSlot.replaceChildren();
        this.purchasePrice = document.createElement('strong');
        this.purchaseNote = document.createElement('small');
        this.priceSlot.append(this.purchasePrice, this.purchaseNote);
        this.purchaseButton = document.createElement('button');
        this.purchaseButton.type = 'button';
        this.purchaseButton.className = 'button button-primary';
        this.buttonSlot.replaceChildren(this.purchaseButton);
        this.purchaseButton.addEventListener('click', () => {
          purchase.resync();
          const button = this.purchaseSourceButton();
          if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
          // Use SC's clone when present so its validation and product-form's one cart request still run.
          // SC hands the click to its hidden original a few ms later, so watch the form's submit, not this button.
          const form = button.form || button.closest('form');
          clearTimeout(this.purchaseValidationTimer);
          this.purchaseSubmitWatch?.();
          let started = false;
          const markStarted = () => { started = true; };
          form?.addEventListener('submit', markStarted, true);
          this.purchaseSubmitWatch = () => { form?.removeEventListener('submit', markStarted, true); this.purchaseSubmitWatch = null; };
          button.click();
          this.syncPurchase();
          this.purchaseValidationTimer = setTimeout(() => {
            this.purchaseSubmitWatch?.();
            const busy = started || this.purchase.querySelector('.cq-purchase-add.loading, .cq-purchase-add[aria-disabled="true"]');
            if (this.isConnected && !busy) this.revealPurchaseValidation();
          }, 400);
        }, options);
        this.purchaseHandler = () => this.syncPurchase();
        purchase.addEventListener('cq:purchase-total', this.purchaseHandler, options);
        // V0 #14: the 40 px photo follows the chosen option (Liquid renders the selected one on load).
        purchase.addEventListener('cq:variant-change', (event) => this.syncThumb(event.detail?.variant), options);
        this.purchaseObserver = new MutationObserver(() => this.syncPurchase());
        this.purchaseObserver.observe(purchase, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class'] });
        this.purchaseOverlayObserver = new MutationObserver(() => this.schedulePurchaseVisibility());
        this.purchaseOverlayObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open'] });
        this.purchaseResizeObserver = new ResizeObserver(() => this.schedulePurchaseVisibility());
        this.purchaseResizeObserver.observe(this);
        const dock = document.getElementById('mobile-dock');
        if (dock) this.purchaseResizeObserver.observe(dock);
        window.addEventListener('scroll', () => this.schedulePurchaseVisibility(), { ...options, passive: true });
        window.addEventListener('resize', () => this.schedulePurchaseVisibility(), options);
        window.visualViewport?.addEventListener('resize', () => this.schedulePurchaseVisibility(), options);
        document.addEventListener('focusin', () => this.schedulePurchaseVisibility(), options);
        document.addEventListener('focusout', () => this.schedulePurchaseVisibility(), options);
        // STICKY-3 (WCAG 2.4.11): a control focused inside an app's own scroll box (the Stamped reviews carousel, which
        // also scrolls the page itself after focus) can land under the shown bar, since the root scroll-padding only
        // steers the browser's own focus scroll. Once the page has stopped scrolling (150 ms quiet, up to 1.5 s after
        // the focus), a keyboard-focused control still under the bar is lifted just clear of it.
        const clearBar = (target) => {
          if (!target || document.activeElement !== target || !this.classList.contains('is-visible') || !target.matches(':focus-visible')) return;
          const box = target.getBoundingClientRect();
          const edge = this.getBoundingClientRect().top;
          if (box.bottom <= edge || box.top >= window.innerHeight || box.height >= edge - this.getHeaderOffset()) return;
          for (let e = target; e && e !== document.body; e = e.parentElement) if (getComputedStyle(e).position === 'fixed') return;
          window.scrollBy({ top: box.bottom - edge + 8, behavior: 'instant' });
        };
        const settle = () => { clearTimeout(this.focusSettle); this.focusSettle = setTimeout(() => clearBar(this.focusTarget), 150); };
        document.addEventListener('focusin', (event) => {
          if (!(event.target instanceof Element) || this.contains(event.target)) return;
          this.focusTarget = event.target;
          this.focusUntil = Date.now() + 1500;
          settle();
        }, options);
        window.addEventListener('scroll', () => { if (this.focusTarget && Date.now() < this.focusUntil) settle(); }, { ...options, passive: true });
        document.addEventListener('bundle-bar:change', () => this.schedulePurchaseVisibility(), options);
        this.setupErrorMessageSync();
        this.syncPurchase();
      }

      purchaseSourceButton() {
        return this.purchase.querySelector('.cq-purchase-add.bold_clone') || this.purchase.querySelector('.cq-purchase-add');
      }

      revealPurchaseValidation() {
        const visible = (element) => element.getClientRects().length && !element.closest('[hidden], [inert], .bold_option_hidden');
        const invalid = [...this.purchase.querySelectorAll('.bold_options :is(:invalid, [aria-invalid="true"], .bold_option_error)')].find(visible);
        const target = invalid || this.purchase;
        const controls = 'input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex]:not([tabindex="-1"])';
        const focusTarget = target.matches(controls) ? target : [...target.querySelectorAll(controls)].find(visible) || target;
        if (!focusTarget.matches(controls) && !focusTarget.hasAttribute('tabindex')) {
          focusTarget.setAttribute('tabindex', '-1');
          focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), { once: true });
        }
        target.scrollIntoView({ block: 'center' });
        focusTarget.focus({ preventScroll: true });
      }

      // The option's own photo from the product gallery, at the bar's 40/80/120 widths; no photo of its own keeps
      // the current one. The img keeps loading=lazy, and the data-cq-seen gate still holds it until the first show.
      syncThumb(variant) {
        const img = this.querySelector('.cq-sticky-thumb img');
        const id = variant?.mediaId;
        if (!img || !id || img.dataset.cqMedia === String(id)) return;
        // v6 gallery templates (2026-10-29): a photo the chosen option hides sits inside the gallery's <template> until
        // asked for (assets/media-gallery.js materialize()); it is brought in first so this lookup finds the same
        // photo it found when every photo was in the page. A no-op for a photo already live.
        document.querySelector('media-gallery')?.materialize?.([id]);
        const source = document.querySelector(`.product-media-main[data-media-id="${id}"] img, [data-media-id="${id}"] img`);
        const src = source?.getAttribute('src');
        if (!src) return;
        const at = (width) => {
          const url = new URL(src, location.href);
          url.searchParams.set('width', width);
          return url.href.replace(/^https?:/, '');
        };
        img.dataset.cqMedia = String(id);
        img.srcset = [40, 80, 120].map((width) => `${at(width)} ${width}w`).join(', ');
        img.src = at(120);
      }

      syncPurchase() {
        const total = this.purchase.purchaseTotal;
        const source = this.purchaseSourceButton();
        if (!total || !source) return;
        if (this.purchasePrice.textContent !== total.totalText) this.purchasePrice.textContent = total.totalText;
        if (this.purchaseNote.textContent !== total.note) this.purchaseNote.textContent = total.note;
        if (this.purchaseButton.textContent !== total.buttonLabel) this.purchaseButton.textContent = total.buttonLabel;
        const busy = source.classList.contains('loading') || source.getAttribute('aria-disabled') === 'true';
        this.purchaseButton.disabled = !total.available || source.disabled || busy;
        this.purchaseButton.setAttribute('aria-busy', String(busy));
        this.schedulePurchaseVisibility();
      }

      schedulePurchaseVisibility() {
        // INP: the reads run after the next paint. A frame callback runs before the browser lays the frame out, so a
        // read there lays the whole page out again whenever a tap changed any markup (about 60 ms on the first tap on a
        // slow phone). The bar's state follows one frame later, which no shopper can see.
        if (this.purchasePending) return;
        this.purchasePending = true;
        cancelAnimationFrame(this.purchaseFrame);
        this.purchaseFrame = requestAnimationFrame(() => {
          clearTimeout(this.purchaseTimer);
          this.purchaseTimer = setTimeout(() => {
            this.purchasePending = false;
            this.updatePurchaseVisibility();
          }, 0);
        });
      }

      updatePurchaseVisibility() {
        // A7 4a: all reads, then the writes, each only when changed, so a scroll frame lays out once. Phone only (the
        // bar is hidden from 750 px). body.cq-buybar-on only lifts chat, so the bar measures the same before it.
        let visible = false;
        let bottom = '';
        let height = '';
        if (this.phoneQuery.matches) {
          const button = this.purchaseSourceButton();
          const price = this.purchase.summaryAll('[data-cq-price]')[0];
          const priceBox = price?.getBoundingClientRect();
          const buttonBox = button?.getBoundingClientRect();
          const top = this.getHeaderOffset();
          const typing = document.activeElement?.matches('textarea, input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"])');
          const dock = document.getElementById('mobile-dock');
          bottom = `${dock?.getBoundingClientRect().height || 0}px`;
          visible = Boolean(priceBox?.height && buttonBox?.height)
            && priceBox.bottom <= top && buttonBox.bottom <= top && !this.purchase.purchaseTotal?.callForPrice
            && !typing && !document.querySelector('dialog[open], .js-custom-bundle-bar.is-pinned');
          if (visible) {
            // The measured box already includes safe-area padding; CSS adds that inset once when lifting chat.
            const padding = getComputedStyle(this.container);
            const safeArea = Math.max(0, parseFloat(padding.paddingBottom) - parseFloat(padding.paddingTop));
            height = `${Math.max(0, this.getBoundingClientRect().height - safeArea)}px`;
          }
        }
        // Writes only below this line.
        if (bottom && bottom !== this.dockBottom) this.style.bottom = (this.dockBottom = bottom);
        if (visible !== document.body.classList.contains('cq-buybar-on')) document.body.classList.toggle('cq-buybar-on', visible);
        if (visible) {
          if (height !== this.buybarHeight) document.body.style.setProperty('--cq-buybar-height', (this.buybarHeight = height));
          if (bottom !== this.buybarBottom) document.body.style.setProperty('--cq-buybar-bottom', (this.buybarBottom = bottom));
        } else if (this.buybarHeight || this.buybarBottom) {
          document.body.style.removeProperty('--cq-buybar-height');
          document.body.style.removeProperty('--cq-buybar-bottom');
          this.buybarHeight = this.buybarBottom = '';
        }
        if (visible === this.classList.contains('is-visible')) return;
        this.classList.toggle('is-visible', visible);
        // The photo loads on the first show (section-sticky-cart.css, A0 R10).
        if (visible) this.toggleAttribute('data-cq-seen', true);
        this.inert = !visible;
        this.setAttribute('aria-hidden', String(!visible));
      }

      findProductElements() {
        // Find the main product section
        const ownedForm = this.dataset.cqFormId ? document.getElementById(this.dataset.cqFormId) : null;
        this.mainProductSection = ownedForm?.closest('.product-info-wrap') || document.querySelector(".product-info-wrap");
        if (!this.mainProductSection) return false;

        // Find the product form
        this.mainProductForm = ownedForm?.closest('product-form') || this.mainProductSection.querySelector("product-form");
        if (!this.mainProductForm) return false;

        // Find the add to cart button
        this.mainAddToCartButton = this.mainProductForm.querySelector(".button-add-to-cart");

        // Find the main quantity input
        this.mainQuantityInput = this.mainProductForm.querySelector(".quantity-input-field");

        // Check if product has variants
        const variantPicker = this.mainProductSection.querySelector(
          "variant-radios, variant-selects",
        );
        this.productHasVariants =
          variantPicker && !variantPicker.closest(".product-variants.variants-hidden");

        return true;
      }

      detectMobileDock() {
        this.mobileDock = document.getElementById("mobile-dock");
        if (this.mobileDock) {
          this.classList.add("has-mobile-dock");
          this.updateBottomPosition();

          // Watch for mobile dock height changes
          const resizeObserver = new ResizeObserver(() => {
            this.updateBottomPosition();
          });
          resizeObserver.observe(this.mobileDock);
        }
      }

      updateBottomPosition() {
        if (this.mobileDock) {
          const dockHeight = this.mobileDock.offsetHeight;
          this.style.bottom = `${dockHeight}px`;
        } else {
          this.style.bottom = "0";
        }
      }

      cloneProductContent() {
        this.cloneProductImage();
        this.cloneProductTitle();
        this.cloneProductPrice();

        if (this.productHasVariants) {
          // Hide price for variant products
          if (this.priceSlot) {
            this.priceSlot.style.display = "none";
          }
          this.createSelectOptionsButton();
        } else {
          this.cloneQuantityInput();
          this.cloneAddToCartButton();
        }
      }

      cloneProductImage() {
        if (!this.showImage || !this.imageSlot) return;
        const mainImage = this.mainProductSection.querySelector(
          ".product-media-main.is-active img",
        );
        if (mainImage) {
          const imgClone = document.createElement("img");
          imgClone.src = mainImage.src;
          imgClone.alt = mainImage.alt || "";
          imgClone.loading = "lazy";
          this.imageSlot.appendChild(imgClone);
        }
      }

      cloneProductTitle() {
        const mainTitle = this.mainProductSection.querySelector(".product-info-heading");
        if (mainTitle && this.titleSlot) {
          this.titleSlot.textContent = mainTitle.textContent.trim();
        }
      }

      cloneProductPrice() {
        const priceElement = this.mainProductSection.querySelector('[id^="price-"]');
        if (priceElement && this.priceSlot) {
          const priceClone = priceElement.cloneNode(true);
          priceClone.removeAttribute("id");
          this.priceSlot.appendChild(priceClone);
        }
      }

      cloneQuantityInput() {
        if (!this.quantitySlot) return;
        const mainQuantity = this.mainProductForm.querySelector("quantity-input");
        if (mainQuantity) {
          const quantityClone = mainQuantity.cloneNode(true);

          // Remove IDs to avoid conflicts
          const inputs = quantityClone.querySelectorAll("[id]");
          inputs.forEach((input) => input.removeAttribute("id"));

          this.quantitySlot.appendChild(quantityClone);

          // Store reference to sticky quantity input
          this.stickyQuantityInput = quantityClone.querySelector(".quantity-input-field");

          // Sync quantity changes
          this.setupQuantitySync(quantityClone);
        }
      }

      cloneAddToCartButton() {
        if (!this.buttonSlot || !this.mainAddToCartButton) return;

        const buttonClone = document.createElement("button");
        buttonClone.type = "button";
        buttonClone.className = this.mainAddToCartButton.className;
        buttonClone.innerHTML = this.mainAddToCartButton.innerHTML;

        // Remove any IDs
        buttonClone.removeAttribute("id");

        // Sync initial disabled state
        if (
          this.mainAddToCartButton.disabled ||
          this.mainAddToCartButton.hasAttribute("disabled")
        ) {
          buttonClone.disabled = true;
          buttonClone.setAttribute("disabled", "");
        }

        // When clicked, sync quantity first then trigger the main form submission
        buttonClone.addEventListener("click", (e) => {
          e.preventDefault();
          if (!buttonClone.disabled && this.mainAddToCartButton) {
            // Force sync quantity before submitting
            if (this.stickyQuantityInput && this.mainQuantityInput) {
              this.mainQuantityInput.value = this.stickyQuantityInput.value;
            }
            this.mainAddToCartButton.click();
            // Sync loading state periodically
            this.syncButtonLoadingState();
          }
        });

        this.buttonSlot.appendChild(buttonClone);
        this.stickyAddToCartButton = buttonClone;
      }

      syncButtonLoadingState() {
        if (!this.stickyAddToCartButton || !this.mainAddToCartButton) return;
        const stickySpinner = this.stickyAddToCartButton.querySelector(".button-overlay-spinner");
        if (!stickySpinner) return;

        if (this.mainAddToCartButton.classList.contains("loading")) {
          this.stickyAddToCartButton.classList.add("loading");
          stickySpinner.classList.remove("hidden");
        } else {
          this.stickyAddToCartButton.classList.remove("loading");
          stickySpinner.classList.add("hidden");
        }
      }

      createSelectOptionsButton() {
        if (!this.buttonSlot) return;
        const selectButton = document.createElement("button");
        selectButton.type = "button";
        selectButton.className = "button button-secondary button-select-options";
        selectButton.textContent = this.selectOptionsText;

        selectButton.addEventListener("click", () => {
          this.scrollToProductForm();
        });

        this.buttonSlot.appendChild(selectButton);

        // Hide quantity slot since we're showing select options
        if (this.quantitySlot) {
          this.quantitySlot.style.display = "none";
        }
      }

      scrollToProductForm() {
        const productInfo = document.querySelector(".product-info");
        if (productInfo) {
          const headerOffset = this.getHeaderOffset();
          const elementPosition = productInfo.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset - 20;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }

      getHeaderOffset() {
        const bar = document.querySelector(".cq-header-bar");
        const legacy = document.querySelector(".section-header");
        const legacyEnabled = legacy?.matches(".section-header-sticky, .section-header-sticky-always") &&
          !legacy.querySelector('sticky-header[disabled="true"]');
        const header = bar || (legacyEnabled ? legacy : null);
        const rect = header?.getBoundingClientRect();
        return rect?.width && rect.height && rect.top < window.innerHeight
          ? Math.max(0, Math.min(rect.bottom, window.innerHeight)) : 0;
      }

      setupQuantitySync(stickyQuantity) {
        if (!this.mainQuantityInput || !this.stickyQuantityInput) return;

        let isSyncing = false;

        // Sync function to avoid duplication
        const syncStickyToMain = () => {
          if (isSyncing) return;
          isSyncing = true;
          this.mainQuantityInput.value = this.stickyQuantityInput.value;
          this.mainQuantityInput.dispatchEvent(new Event("change", { bubbles: true }));
          setTimeout(() => {
            isSyncing = false;
          }, 10);
        };

        const syncMainToSticky = () => {
          if (isSyncing) return;
          isSyncing = true;
          this.stickyQuantityInput.value = this.mainQuantityInput.value;
          setTimeout(() => {
            isSyncing = false;
          }, 10);
        };

        // Sync sticky to main on input and change
        this.stickyQuantityInput.addEventListener("input", syncStickyToMain);
        this.stickyQuantityInput.addEventListener("change", syncStickyToMain);

        // Sync main to sticky on input and change
        this.mainQuantityInput.addEventListener("input", syncMainToSticky);
        this.mainQuantityInput.addEventListener("change", syncMainToSticky);

        // Watch for attribute changes (some quantity inputs update via setAttribute)
        const stickyObserver = new MutationObserver(() => {
          syncStickyToMain();
        });

        stickyObserver.observe(this.stickyQuantityInput, {
          attributes: true,
          attributeFilter: ["value"],
        });

        const mainObserver = new MutationObserver(() => {
          syncMainToSticky();
        });

        mainObserver.observe(this.mainQuantityInput, {
          attributes: true,
          attributeFilter: ["value"],
        });
      }

      setupScrollDetection() {
        if (this.triggerType === "scroll_past_button") {
          this.setupIntersectionObserver();
        } else {
          this.setupScrollListener();
        }
      }

      setupIntersectionObserver() {
        if (!this.mainAddToCartButton && !this.buttonSlot.querySelector(".button-select-options"))
          return;
        const targetButton = this.mainAddToCartButton || this.mainProductForm;
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.hide();
              } else if (entry.boundingClientRect.top < 0) {
                this.show();
              }
            });
          },
          {
            threshold: 0,
            rootMargin: "0px",
          },
        );
        observer.observe(targetButton);
      }

      setupScrollListener() {
        let ticking = false;
        window.addEventListener("scroll", () => {
          if (!ticking) {
            window.requestAnimationFrame(() => {
              if (window.scrollY > this.triggerOffset) {
                this.show();
              } else {
                this.hide();
              }
              ticking = false;
            });
            ticking = true;
          }
        });
      }

      setupEventListeners() {
        // Listen for cart additions to sync button state
        document.addEventListener("cart:item-add", () => {
          // Reset button state after successful add to cart
          this.resetButtonState();
        });

        // Sync button loading state during submission
        if (this.mainProductForm) {
          this.mainProductForm.addEventListener("submit", () => {
            const loadingSync = setInterval(() => {
              this.syncButtonLoadingState();
              // Stop syncing after button is no longer loading
              if (!this.mainAddToCartButton.classList.contains("loading")) {
                clearInterval(loadingSync);
              }
            }, 50);
          });
        }
      }

      reinitializeAfterVariantChange() {
        // Refresh element references (DOM gets replaced via AJAX)
        this.findProductElements();

        // Update the variant image and refresh error sync
        this.updateVariantImage();
        this.setupErrorMessageSync();

        //Setup scroll detection to watch the new button after AJAX
        this.setupScrollDetection();
      }

      updateVariantImage() {
        if (!this.showImage || !this.imageSlot) return;

        const mainImage = this.mainProductSection.querySelector(
          ".product-media-main.is-active img",
        );
        const currentImg = this.imageSlot.querySelector("img");

        if (mainImage && currentImg) {
          currentImg.src = mainImage.src;
          currentImg.alt = mainImage.alt || "";
        }
      }

      updatePrice() {
        const mainPrice = this.mainProductSection.querySelector('[id^="price-"]');
        if (mainPrice && this.priceSlot) {
          const priceClone = mainPrice.cloneNode(true);
          priceClone.removeAttribute("id");
          this.priceSlot.innerHTML = "";
          this.priceSlot.appendChild(priceClone);
        }
      }

      updateButtonState() {
        if (!this.stickyAddToCartButton || !this.mainAddToCartButton) return;
        const mainButton = this.mainAddToCartButton;

        // Sync disabled state and attribute
        if (mainButton.disabled || mainButton.hasAttribute("disabled")) {
          this.stickyAddToCartButton.disabled = true;
          this.stickyAddToCartButton.setAttribute("disabled", "");
        } else {
          this.stickyAddToCartButton.disabled = false;
          this.stickyAddToCartButton.removeAttribute("disabled");
        }

        // Sync button text
        const mainButtonText = mainButton.querySelector("span");
        const stickyButtonText = this.stickyAddToCartButton.querySelector("span");
        if (mainButtonText && stickyButtonText) {
          stickyButtonText.textContent = mainButtonText.textContent;
        }
      }

      updateImage(media) {
        if (!this.imageSlot) return;
        const img = this.imageSlot.querySelector("img");
        if (img && media.preview_image) {
          img.src = media.preview_image.src;
          img.alt = media.alt || "";
        }
      }

      resetButtonState() {
        if (this.stickyAddToCartButton) {
          const spinner = this.stickyAddToCartButton.querySelector(".button-overlay-spinner");
          if (spinner) {
            spinner.classList.add("hidden");
          }
          this.stickyAddToCartButton.classList.remove("loading");
          this.stickyAddToCartButton.removeAttribute("aria-disabled");
        }
      }

      show() {
        this.wantsVisible = true;
        this.applyVisibility();
      }

      hide() {
        this.wantsVisible = false;
        this.applyVisibility();
      }

      applyVisibility() {
        const visible = this.wantsVisible && !this.suppressed;
        // The scroll trigger calls show()/hide() every frame; both of the calls below
        // force a layout, so only run them when the state actually flips.
        if (visible === this.classList.contains("is-visible")) return;
        this.classList.toggle("is-visible", visible);
        this.adjustBodyPadding();
        this.adjustButtonTopPosition();
      }

      /**
       * A custom bundle section's mobile bar takes this cart's slot above the mobile dock
       * while it is pinned there; once it settles into the flow at the end of its section,
       * or scrolls away, the cart is free again. Recount the marked bars rather than
       * trusting the event, so several bundle sections on one page can't leave the cart
       * suppressed after one of them unpins.
       */
      watchBundleBar() {
        this.bundleBarHandler = () => {
          this.suppressed = !!document.querySelector(".js-custom-bundle-bar.is-pinned");
          this.applyVisibility();
        };
        document.addEventListener("bundle-bar:change", this.bundleBarHandler);
        this.bundleBarHandler();
      }

      adjustButtonTopPosition() {
        const buttonTop = document.querySelector(".button-top");
        if (!buttonTop) return;

        if (this.classList.contains("is-visible")) {
          const stickyCartHeight = this.offsetHeight;
          buttonTop.style.bottom = `${stickyCartHeight + 20}px`;
        } else {
          buttonTop.style.bottom = "20px";
        }
      }

      adjustBodyPadding() {
        if (this.classList.contains("is-visible")) {
          const stickyCartHeight = this.offsetHeight;
          document.body.style.paddingBottom = `${stickyCartHeight}px`;
        } else {
          document.body.style.paddingBottom = "";
        }
      }

      setupResizeListener() {
        let resizeTimeout;
        window.addEventListener("resize", () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.adjustBodyPadding();
            this.adjustButtonTopPosition();
          }, 150);
        });

        // Handle sticky cart height changes (error messages, content updates)
        const stickyCartResizeObserver = new ResizeObserver(() => {
          if (this.classList.contains("is-visible")) {
            this.adjustBodyPadding();
            this.adjustButtonTopPosition();
          }
        });
        stickyCartResizeObserver.observe(this);
      }

      setupErrorMessageSync() {
        if (!this.mainProductForm || !this.errorSlot) return;
        const mainErrorWrapper = this.mainProductForm.querySelector(
          ".product-form-error-message-wrapper",
        );
        if (!mainErrorWrapper) return;

        // Watch for error message changes
        const observer = new MutationObserver(() => {
          this.syncErrorMessage(mainErrorWrapper);
        });

        this.errorObserver?.disconnect();
        this.errorObserver = observer;
        observer.observe(mainErrorWrapper, {
          attributes: true,
          attributeFilter: ["hidden"],
          childList: true,
          subtree: true,
        });

        // Initial sync
        this.syncErrorMessage(mainErrorWrapper);
      }

      syncErrorMessage(mainErrorWrapper) {
        if (!this.errorSlot) return;

        // Clear previous timeout
        clearTimeout(this.errorFadeTimeout);
        const isHidden = mainErrorWrapper.hasAttribute("hidden");
        this.errorSlot.style.transition = "opacity 0.3s ease";

        if (isHidden) {
          this.errorSlot.innerHTML = "";
          this.errorSlot.style.opacity = "1";
          return;
        }

        // Insert fresh error content
        const errorClone = mainErrorWrapper.cloneNode(true);
        this.errorSlot.innerHTML = "";
        this.errorSlot.appendChild(errorClone);
        this.errorSlot.style.opacity = "1";

        // Schedule fade out
        this.errorFadeTimeout = setTimeout(() => {
          this.errorSlot.style.opacity = "0";
          const onTransitionEnd = () => {
            this.errorSlot.innerHTML = "";
            this.errorSlot.style.opacity = "1";
            this.errorSlot.removeEventListener("transitionend", onTransitionEnd);
          };
          this.errorSlot.addEventListener("transitionend", onTransitionEnd);
        }, 5000);
      }

      disconnectedCallback() {
        this.purchaseEvents?.abort();
        this.purchaseObserver?.disconnect();
        this.errorObserver?.disconnect();
        clearTimeout(this.errorFadeTimeout);
        this.purchaseOverlayObserver?.disconnect();
        this.purchaseResizeObserver?.disconnect();
        cancelAnimationFrame(this.purchaseFrame);
        clearTimeout(this.purchaseTimer);
        this.purchasePending = false;
        clearTimeout(this.purchaseValidationTimer);
        clearTimeout(this.focusSettle);
        this.purchaseSubmitWatch?.();
        if (this.purchase) {
          if (!document.querySelector('.sticky-cart.cq-sticky-purchase')) document.documentElement.classList.remove('cq-buybar-page');
          document.body.classList.remove('cq-buybar-on');
          document.body.style.removeProperty('--cq-buybar-height');
          document.body.style.removeProperty('--cq-buybar-bottom');
        }
        if (this.unsubscribeVariantChange) {
          this.unsubscribeVariantChange();
        }
        if (this.bundleBarHandler) {
          document.removeEventListener("bundle-bar:change", this.bundleBarHandler);
        }
      }
    },
  );
}
